/* Access rules for brand users are initialized once per login/workspace switch and are taken
   from the backend response as it is. If that response is inconsistent (user_type stays "brand"
   while role, permissions and sections belong to the customer workspace), a brand user ends up
   with customer rights. The scope of a brand user is therefore pinned here and cross checked
   against the values the backend reports. */

export const BRAND_USER_TYPE = "brand";

// the only sections a brand user may ever reach: campaigns module entry + campaign list
export const BRAND_ALLOWED_SECTIONS = ["campaigns_section", "campaign_list"];

// the only role a brand user may ever have
export const BRAND_EXPECTED_ROLE = "brand";

// every section keyword the platform knows (see allowedNavOptions)
export const PLATFORM_SECTIONS = [
  "overview",
  "customDashboard",
  "campaigns_section",
  "campaign_list",
  "clips",
  "pictures",
  "listingCheck",
  "capacityCheck",
  "retail_analytics_section",
  "general_analytics",
  "instore_duration",
  "profitability",
  "trolley_usage",
  "heatmap",
  "optimization",
  "infrastructure_section",
  "stores_infra",
  "monitoring_infra",
  "data_exchange_section",
  "data_exchange_upload",
  "data_exchange_download",
  "user_section",
  "user_list",
  "role_list",
];

// sections the backend has to report as deactivated for a brand user
export const brandExpectedDeactivatedSections = () =>
  PLATFORM_SECTIONS.filter((section) => !BRAND_ALLOWED_SECTIONS.includes(section));

const normalizedValue = (value) => (typeof value === "string" ? value.trim().toLowerCase() : "");

// compares the deactivated sections of the backend against the set defined above
const reportedSectionsMatch = (reportedSections) => {
  if (Array.isArray(reportedSections) === false) {
    return false;
  }
  const expectedSections = brandExpectedDeactivatedSections();
  const uniqueReportedSections = [...new Set(reportedSections.map((section) => normalizedValue(section)))];

  return (
    uniqueReportedSections.length === expectedSections.length &&
    expectedSections.every((section) => uniqueReportedSections.includes(normalizedValue(section)))
  );
};

/* verifies the backend response of a brand user against the hardcoded role and section scope.
   Both sides have to be identical, a difference blocks the access completely. */
export const verifyBrandAccess = (userDetail, userRole) => {
  const detail = typeof userDetail !== "undefined" && userDetail !== null ? userDetail : {};

  if (normalizedValue(detail.user_type) !== BRAND_USER_TYPE) {
    return {
      is_brand_user: false,
      access_denied: false,
      reason: "",
    };
  }

  const assignedRoles = Array.isArray(detail.assigned_user_roles) ? detail.assigned_user_roles : [];
  const reportedRoles = [detail.user_role, userRole, ...assignedRoles];

  if (reportedRoles.some((role) => normalizedValue(role) !== BRAND_EXPECTED_ROLE)) {
    return {
      is_brand_user: true,
      access_denied: true,
      reason: "Role reported by the backend does not match the expected brand role.",
    };
  }

  if (reportedSectionsMatch(detail.deactivated_sections) === false) {
    return {
      is_brand_user: true,
      access_denied: true,
      reason: "Deactivated sections reported by the backend do not match the brand section scope.",
    };
  }

  return {
    is_brand_user: true,
    access_denied: false,
    reason: "",
  };
};

// a page is only reachable for a brand user if all of its sections belong to the brand scope
export const brandSectionAccessAllowed = (sectionCheck) =>
  Array.isArray(sectionCheck) &&
  sectionCheck.length !== 0 &&
  sectionCheck.every((section) => BRAND_ALLOWED_SECTIONS.includes(section));
